<template>
  <div :class="$style.wrapper">
    <div :class="[$style.container, isModal && $style.containerInModal]">
      <p v-if="isModal" :class="$style.backLink"><button @click.prevent="close">&lt; 戻る</button></p>
      <div :class="$style.content">
        <p v-if="isSignin" :class="$style.signup"><a :href="`/signup?from=${from}`" @click.prevent="onClick('signup', $event)">会員登録はこちら</a></p>
        <p v-else :class="$style.signup"><a :href="`/signin?from=${from}`" @click.prevent="onClick('signin', $event)">ログインはこちら</a></p>
        <h1 :class="$style.pageTitle">{{ pageTitle }}</h1>
        <div v-if="oauthProvider && socialProvider(oauthProvider)" :class="$style.socialConnected">
          <!-- eslint-disable vue/no-v-html -->
          <div :class="$style.socialConnectedSvg" v-html="socialProvider(oauthProvider)!.svg"></div>
          <!-- eslint-enable vue/no-v-html -->
          <div>{{ socialProvider(oauthProvider)!.label }}連携しています</div>
        </div>
        <Form @invalid-submit="scrollToError" @submit="onSubmit">
          <ul :class="$style.formInner">
            <li>
              <ITextField v-model.trim="formData.email" name="メールアドレス" title="メールアドレス" type="email" autocomplete="email" rules="required|email|max:200" />
            </li>
            <li>
              <ITextField v-model.trim="formData.password" name="パスワード" title="パスワード" type="password" :autocomplete="isSignin ? 'current-password' : 'new-password'" rules="required|min:8|max:16|alpha_num" />
            </li>
            <li v-if="!isSignin">
              <ITextField v-model.trim="formData.memberName" name="ユーザー名" title="ユーザー名" autocomplete="username" rules="required|max:32|member_name" />
            </li>
          </ul>
          <div v-if="isSignin">
            <div :class="$style.remember">
              <label><input v-model="formData.remember" type="checkbox" /> 設定を保存</label>
            </div>
            <IButton type="primary" native-type="submit" :class="$style.loginBtn">ログインする</IButton>
            <p :class="$style.forget"><a href="/signin/forget/password" :class="$style.forgetLink">パスワードをお忘れの方</a></p>
          </div>
          <div v-else>
            <div :class="$style.subscribeNewsletter">
              <label><input v-model="formData.subscribeNewsletter" type="checkbox" /> メールマガジンを購読する</label>
              <p :class="$style.note">
                会員登録には、<a href="/rule" target="_blank" class="text-decoration-underline">利用規約</a>と<a href="/privacy" target="_blank" class="text-decoration-underline">プライバシーポリシー</a>への同意が必要です。
              </p>
            </div>
            <IButton type="primary" native-type="submit" :class="$style.loginBtn">同意して会員登録する</IButton>
          </div>
          <template v-if="!isWebView && oauthProvider === undefined">
            <p :class="$style.or"><span :class="$style.orInner">または</span></p>
            <ul :class="$style.socialBtns">
              <li v-for="item in connectArr" :key="item.id" :class="$style.socialBtn">
                <!-- eslint-disable vue/no-v-html -->
                <a :href="item.url" :class="$style.socialBtnLink"
                  ><span :class="$style.socialBtnSvg"><span v-html="item.svg"></span></span>{{ item.label }}で{{ isSignin ? 'ログイン' : '登録' }}</a
                >
                <!-- eslint-enable vue/no-v-html -->
              </li>
            </ul>
          </template>
          <p>
            <a :href="helpLink" target="_blank">{{ pageTitle }}でお困りの方</a>
          </p>
        </Form>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { Form, type SubmissionHandler } from 'vee-validate';
import { reactive, computed } from 'vue';
import { scrollToError } from '@/js/lib/helper/validate';
import { signin, signup } from '@/js/lib/api/v1/user';
import { checkEmailExistence } from '@/js/lib/api/v1/validate';
import { useCommonStore } from '@/js/lib/store/common';
import { useUserStore } from '@/js/lib/store/user';
import { info, loading } from '@/js/lib/ui';
import IButton from '@/js/components/molecules/IButton.vue';
import ITextField from '@/js/components/molecules/ITextField.vue';

interface FormData {
  email: string;
  password: string;
  memberName: string;
  remember: boolean;
  subscribeNewsletter: boolean;
}

const props = withDefaults(
  defineProps<{
    modelValue?: string;
    email?: string;
    memberName?: string;
    transactionToken?: string;
    oauthProvider?: string;
    isModal?: boolean;
    from?: string;
  }>(),
  {
    modelValue: '',
    email: '',
    memberName: '',
    transactionToken: '',
    oauthProvider: undefined,
    from: '',
  },
);
const emit = defineEmits<{
  (e: 'update:modelValue', name: string): void;
  (e: 'close'): void;
  (e: 'submit', value: string): void;
}>();
const socialProviders = [
  {
    id: 'facebook',
    label: 'Facebook',
    svg: '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 48 48"><path d="M48 24C48 10.7438 37.2562 0 24 0C10.7438 0 0 10.7438 0 24C0 35.9813 8.775 45.9094 20.25 47.7094V30.9375H14.1562V24H20.25V18.7125C20.25 12.6984 23.8313 9.375 29.3156 9.375C31.9406 9.375 34.6875 9.84375 34.6875 9.84375V15.75H31.6594C28.6781 15.75 27.75 17.6016 27.75 19.5V24H34.4062L33.3422 30.9375H27.75V47.7094C39.225 45.9094 48 35.9813 48 24Z" fill="#1877F2"/><path d="M33.3422 30.9375L34.4062 24H27.75V19.5C27.75 17.6016 28.6781 15.75 31.6594 15.75H34.6875V9.84375C34.6875 9.84375 31.9406 9.375 29.3156 9.375C23.8313 9.375 20.25 12.6984 20.25 18.7125V24H14.1562V30.9375H20.25V47.7094C21.4734 47.9016 22.725 48 24 48C25.275 48 26.5266 47.9016 27.75 47.7094V30.9375H33.3422Z" fill="white"/></svg>',
  },
  {
    id: 'twitter',
    label: 'X',
    svg: '<svg xmlns="http://www.w3.org/2000/svg" width="18" height="24" viewBox="0 0 1200 1227"><path d="M714.163 519.284L1160.89 0H1055.03L667.137 450.887L357.328 0H0L468.492 681.821L0 1226.37H105.866L515.491 750.218L842.672 1226.37H1200L714.137 519.284H714.163ZM569.165 687.828L521.697 619.934L144.011 79.6944H306.615L611.412 515.685L658.88 583.579L1055.08 1150.3H892.476L569.165 687.854V687.828Z" fill="black"/></svg>',
  },
];
const { isWebView } = useCommonStore();
const userStore = useUserStore();
const formData = reactive<FormData>({
  email: props.email,
  password: '',
  remember: true,
  memberName: props.memberName,
  subscribeNewsletter: true,
});
const isSignin = computed(() => props.modelValue === 'signin');
const pageTitle = computed(() => (isSignin.value ? 'ログイン' : '会員登録'));
const connectArr = computed(() =>
  socialProviders.map((item) => {
    const from = props.from ? `?from=${props.from}` : '';
    const url = `/${props.modelValue}/with/${item.id}${from}`;
    return {
      id: item.id,
      label: item.label,
      svg: item.svg,
      url,
    };
  }),
);
const helpLink = computed(() => (isSignin.value ? 'https://help.iichi.com/hc/ja/articles/360000013721' : 'https://help.iichi.com/hc/ja/articles/200196409'));
const socialProvider = computed(() => (provider: string) => socialProviders.find((p) => p.id === provider));
const onSubmit: SubmissionHandler = async (_values, { setFieldError }) => {
  const emailExistence = await checkEmailExistence({ email: formData.email });
  if (!isSignin.value && emailExistence !== true) {
    setFieldError('メールアドレス', emailExistence);
    scrollToError();
    return;
  }
  loading({
    asyncFunc: async () => {
      if (isSignin.value) {
        const res = await signin({
          email: formData.email,
          password: formData.password,
          remember: formData.remember,
        });
        if (res.error === false) {
          userStore.setSignined();
          if (props.isModal) {
            emit('submit', props.modelValue);
          } else {
            window.location.href = props.from || '/';
          }
        } else {
          info('メールアドレス、もしくはパスワードが違います。', { fadeOut: true });
        }
      } else {
        await signup({
          email: formData.email,
          password: formData.password,
          memberName: formData.memberName,
          subscribeNewsletter: formData.subscribeNewsletter,
          transactionToken: props.transactionToken,
        });
        userStore.setSignined();
        if (props.isModal) {
          emit('submit', props.modelValue);
        } else {
          window.location.href = props.from || '/';
        }
      }
    },
  });
};
const onClick = (name: string, event: Event) => {
  if (props.isModal) {
    emit('update:modelValue', name);
  } else {
    window.location.href = (event.target as HTMLAnchorElement).href;
  }
};
const close = () => {
  emit('close');
};
</script>

<style module lang="scss">
.wrapper {
  background-color: var(--color-white);
  margin: 0 auto;
  max-width: 980px;
  padding-top: 10px;

  form a {
    text-decoration: underline;
  }
}

.container {
  align-items: center;
  display: flex;
  font-size: 16px;
  min-height: calc(100vh - 100px);
  padding: 30px 20px;
  position: relative;

  @media (--sp) {
    font-size: 14px;
  }

  &.containerInModal {
    min-height: calc(100vh - 20px);
  }

  a {
    &:hover {
      text-decoration: none;
    }
  }
}

.backLink {
  left: 20px;
  position: absolute;
  top: 25px;

  > button {
    font-size: 16px;

    @media (--sp) {
      font-size: 14px;
    }
  }
}

.content {
  margin: 0 auto;
  max-width: 480px;
  min-height: 814px;
  text-align: center;
  width: 100%;

  @media (--sp) {
    min-height: inherit;
  }
}

.signup,
.signin {
  font-weight: bold;
  margin-bottom: 16px;
  text-align: right;
}

.pageTitle {
  font-size: 24px;
  margin-bottom: 30px;
}

.socialConnected {
  align-items: center;
  display: flex;
  margin-bottom: 25px;

  .socialConnectedSvg {
    margin-right: 8px;

    > svg {
      vertical-align: bottom;
    }
  }
}

.formInner > li {
  margin-bottom: 20px;
}

.subscribeNewsletter {
  margin-bottom: 20px;
  text-align: left;

  label {
    cursor: pointer;
  }
}

.note {
  font-size: 14px;
  margin: 25px 0 0;
}

.remember {
  margin-bottom: 30px;
  text-align: left;

  label {
    cursor: pointer;
  }
}

.loginBtn {
  margin-bottom: 20px;
}

.forget {
  margin-bottom: 20px;
  text-align: center;
}

.forgetLink {
  text-decoration: underline;
}

.or {
  display: flex;
  justify-content: center;
  margin-bottom: 14px;
  padding: 10px 0;
  position: relative;

  &::before {
    border-top: 1px solid var(--color-black);
    content: '';
    position: absolute;
    top: 50%;
    width: 100%;
  }

  .orInner {
    background-color: var(--color-white);
    display: inline-block;
    padding: 0 10px;
    position: relative;
  }
}

.socialBtns {
  padding-bottom: 10px;
}

.socialBtn {
  margin-bottom: 14px;
  position: relative;

  .socialBtnLink {
    border: 1px solid var(--color-black);
    border-radius: 4px;
    display: block;
    padding: 16px;
    text-decoration: none;

    &:hover {
      opacity: 0.8;
    }

    .socialBtnSvg {
      left: 18px;
      position: absolute;
      width: 24px;
    }
  }
}
</style>
